import { router } from "expo-router";
import React, { useCallback, useMemo, useRef } from "react";
import { FlatList, StyleSheet, View, useWindowDimensions } from "react-native";

import FeedScreenSectionHeader from "@/screens/Explore/ExploreFeed/Components/FeedScreenSectionHeader";
import LargeProfileCard from "@/screens/Explore/Components/ProfileCard/LargeProfileCard";
import SmallProfileCard from "@/screens/Explore/Components/ProfileCard/SmallProfileCard";
import ImageLoadingAnimation from "@/Components/Media/User/Images/ImageLoadingAnimation";
import BorderLayout from "@/Components/Layouts/Native/BorderLayout";
import { Spacings } from "@/styling/spacings";
import {
  RecommendationCategory,
  RecommendationCategoryFeed,
  UserRecommendedItem,
} from "@/schemas/user/explore";
import { EXPLORE_CONSTANTS, calculateCardDimensions } from "@/utils/explore";

const NOOP = (): void => {};

type Props = {
  feedCategoryData: RecommendationCategoryFeed;
  isLoading?: boolean;
  isDevTab?: boolean;
};

function FeedScreenSection({
  feedCategoryData,
  isLoading = false,
  isDevTab = false,
}: Props): React.JSX.Element {
  const isNavigatingRef = useRef(false);
  const hasMoreItems = feedCategoryData.items.next !== null;

  // dimensions
  const { width } = useWindowDimensions();
  const { width: itemWidth, height: itemHeight } = useMemo(() => {
    return calculateCardDimensions(
      width, // screen width
      Spacings.s1 * 3, // spacing between cards + screen edges
      2, // numColumns
      EXPLORE_CONSTANTS.ASPECT_RATIO,
      0.9, // scaleFactor
    );
  }, [width]);
  const { width: largeItemWidth, height: largeItemHeight } = useMemo(() => {
    return calculateCardDimensions(
      width,
      Spacings.s1 * 2, // spacing between cards + screen edges
      1, // numColumns
      EXPLORE_CONSTANTS.ASPECT_RATIO,
      1, // scaleFactor
    );
  }, [width]);

  const handleCategoryPress = useCallback(async () => {
    if (isNavigatingRef.current || isLoading) {
      return;
    }
    isNavigatingRef.current = true;

    router.push({
      params: {
        feedCategoryData: JSON.stringify(feedCategoryData),
      },
      pathname: isDevTab
        ? "/(main)/explore-dev/explore-by-category"
        : "/(main)/explore/explore-by-category",
    });

    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 1000);
  }, [isDevTab, feedCategoryData, router, isLoading]);

  const renderListItem = useCallback(
    ({ item, index }: { item: UserRecommendedItem; index: number }) => {
      const isLargeCard =
        feedCategoryData.category === RecommendationCategory.DATING ||
        feedCategoryData.category === RecommendationCategory.FRIENDS;
      const cardWidth = isLargeCard ? largeItemWidth : itemWidth;
      const cardHeight = isLargeCard ? largeItemHeight : itemHeight;

      if (isLoading) {
        return (
          <BorderLayout
            width={cardWidth}
            height={cardHeight}
            radiusTop={8}
            radiusBottom={8}
          >
            <ImageLoadingAnimation width={cardWidth} />
          </BorderLayout>
        );
      }

      const isLastItem = index === feedCategoryData.items.items.length - 1;
      switch (feedCategoryData.category) {
        case RecommendationCategory.DATING:
        case RecommendationCategory.FRIENDS:
          return (
            <LargeProfileCard
              profile={item}
              itemWidth={cardWidth}
              itemHeight={cardHeight}
              cardState={isLastItem && hasMoreItems ? "overlay" : "normal"}
              onNonNormalStateCardClick={
                isLastItem ? handleCategoryPress : NOOP
              }
            />
          );
        default: {
          return (
            <SmallProfileCard
              profile={item}
              itemWidth={cardWidth}
              itemHeight={cardHeight}
              cardState={isLastItem && hasMoreItems ? "overlay" : "normal"}
              onNonNormalStateCardClick={
                isLastItem ? handleCategoryPress : NOOP
              }
            />
          );
        }
      }
    },
    [
      feedCategoryData.category,
      feedCategoryData.items.items.length,
      hasMoreItems,
      isLoading,
      largeItemWidth,
      largeItemHeight,
      itemWidth,
      itemHeight,
      handleCategoryPress,
    ],
  );

  const keyExtractor = useCallback(
    (item: UserRecommendedItem, index: number) =>
      `${feedCategoryData.category}-${item.id}-${index}`,
    [feedCategoryData.category],
  );

  const ItemSeparator = (): React.JSX.Element => (
    <View style={{ width: Spacings.s1 }} />
  );

  return (
    <View style={styles.container}>
      <FeedScreenSectionHeader
        isLoading={isLoading}
        category={feedCategoryData.category}
        onSeeAllPress={handleCategoryPress}
      />
      <View style={styles.profilesContainer}>
        <FlatList
          data={feedCategoryData.items.items}
          renderItem={renderListItem}
          keyExtractor={keyExtractor}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.flatListContainer}
          ItemSeparatorComponent={ItemSeparator}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  profilesContainer: {
    marginTop: Spacings.s1,
    marginBottom: Spacings.s4,
  },
  flatListContainer: {
    paddingHorizontal: Spacings.s1,
  },
});

export default React.memo(FeedScreenSection);
